import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from 'recharts';
import { Plus, Trash2, RefreshCw, GitBranch, Sparkles, ChevronDown, ChevronRight } from 'lucide-react';
import { API } from '../../App';
import { useAlert } from '../../context/AlertContext';

const INTERVENTION_TYPES = [
  'renewable_energy', 'efficiency', 'fuel_switch', 'electrification', 'offset',
];

export default function ScenariosPanel({ siteId }) {
  const { showSuccess, showError, showInfo } = useAlert();
  const [scenarios, setScenarios] = useState([]);
  const [pathway, setPathway] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [expanded, setExpanded] = useState({});

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [s, p] = await Promise.all([
        axios.get(`${API}/mrv/scenarios`),
        axios.get(`${API}/mrv/pathways/projection?target_year=2050${siteId ? `&site_id=${siteId}` : ''}`),
      ]);
      setScenarios(s.data.scenarios || []);
      setPathway(p.data);
    } catch (e) {
      showError(e.response?.data?.detail || 'Failed to load scenarios');
    } finally {
      setLoading(false);
    }
  }, [siteId, showError]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const deleteScenario = async (id) => {
    if (!window.confirm('Delete this scenario?')) return;
    try {
      await axios.delete(`${API}/mrv/scenarios/${id}`);
      showSuccess('Scenario deleted');
      fetchAll();
    } catch (e) {
      showError(e.response?.data?.detail || 'Delete failed');
    }
  };

  const aiGenerate = async () => {
    try {
      showInfo('Asking AI for scenario suggestions…');
      const res = await axios.post(`${API}/mrv/scenarios/ai-generate`, {});
      const proposed = res.data?.scenarios || [];
      if (proposed.length === 0) {
        showError('AI returned no scenarios');
        return;
      }
      // Show in console for now — full UI integration would let user pick & save
      console.log('AI proposed scenarios:', proposed);
      showInfo(`AI proposed ${proposed.length} scenario(s) — see console`);
    } catch (e) {
      showError(e.response?.data?.detail || 'AI generate failed');
    }
  };

  const toggleExpand = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900">Scenarios & pathways</h2>
        <div className="flex gap-2">
          <button onClick={aiGenerate}
                  className="px-3 py-2 text-sm font-medium text-violet-700 bg-violet-50 hover:bg-violet-100 rounded-md inline-flex items-center gap-1">
            <Sparkles className="w-4 h-4" /> AI Suggest
          </button>
          <button onClick={() => setShowCreate(s => !s)}
                  className="px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md text-sm font-medium inline-flex items-center gap-1">
            <Plus className="w-4 h-4" /> New scenario
          </button>
          <button onClick={fetchAll} disabled={loading} className="p-2 text-gray-500 hover:text-gray-700">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Pathway */}
      {pathway?.buckets?.length > 0 && (
        <div className="mb-6 p-4 bg-white border border-gray-200 rounded-md">
          <div className="text-sm font-semibold text-gray-700 mb-2">
            Decarbonisation pathway → {pathway.target_year}
          </div>
          <div className="text-xs text-gray-500 mb-3">
            Current emissions: {Number(pathway.current_emissions_tco2e).toFixed(2)} tCO2e (last 12 mo, calculated)
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={pathway.buckets}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="year" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip formatter={v => `${Number(v).toFixed(2)} tCO2e`} />
              <Legend />
              <Line type="monotone" dataKey="baseline_glide_tco2e" name="Linear glide-path"
                    stroke="#9ca3af" strokeDasharray="5 5" dot={false} />
              <Line type="monotone" dataKey="scenario_proj_tco2e" name="With scenarios"
                    stroke="#059669" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
          {pathway.scenarios_considered?.length > 0 && (
            <div className="text-xs text-gray-500 mt-1">
              Includes scenarios: {pathway.scenarios_considered.join(', ')}
            </div>
          )}
        </div>
      )}

      {showCreate && (
        <CreateScenarioForm
          siteId={siteId}
          onCreated={() => { setShowCreate(false); fetchAll(); }}
          onCancel={() => setShowCreate(false)}
        />
      )}

      {/* Scenarios table */}
      <div className="space-y-2">
        {scenarios.length === 0 && (
          <div className="text-center text-gray-500 py-12 border border-dashed border-gray-300 rounded">
            <GitBranch className="w-8 h-8 mx-auto text-gray-300 mb-2" />
            No scenarios defined yet
          </div>
        )}
        {scenarios.map(s => (
          <ScenarioRow
            key={s.id}
            scenario={s}
            expanded={!!expanded[s.id]}
            onToggle={() => toggleExpand(s.id)}
            onDelete={() => deleteScenario(s.id)}
            onChanged={fetchAll}
          />
        ))}
      </div>
    </div>
  );
}

function ScenarioRow({ scenario, expanded, onToggle, onDelete, onChanged }) {
  const { showSuccess, showError } = useAlert();
  const [interventions, setInterventions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);

  const fetchDetail = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/mrv/scenarios/${scenario.id}`);
      setInterventions(res.data.interventions || []);
    } catch (e) {
      showError(e.response?.data?.detail || 'Failed to load scenario');
    } finally {
      setLoading(false);
    }
  }, [scenario.id, showError]);

  useEffect(() => { if (expanded) fetchDetail(); }, [expanded, fetchDetail]);

  const deleteIntervention = async (iid) => {
    if (!window.confirm('Delete this intervention?')) return;
    try {
      await axios.delete(`${API}/mrv/scenarios/${scenario.id}/interventions/${iid}`);
      showSuccess('Intervention deleted');
      fetchDetail();
      onChanged();
    } catch (e) {
      showError(e.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div className="border border-gray-200 rounded-md overflow-hidden">
      <div className="flex items-center justify-between p-3 bg-white hover:bg-gray-50">
        <button onClick={onToggle} className="flex items-center gap-2 flex-1 text-left">
          {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          <div>
            <div className="font-semibold text-gray-900">{scenario.name}</div>
            <div className="text-xs text-gray-500">
              {scenario.status} · target {scenario.target_year || '—'} · reduction {Number(scenario.total_reduction || 0).toFixed(0)} tCO2e
              {scenario.estimated_cost ? ` · cost ${Number(scenario.estimated_cost).toFixed(0)} ${scenario.currency}` : ''}
            </div>
          </div>
        </button>
        <button onClick={onDelete} className="text-red-500 hover:text-red-700 p-1">
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      {expanded && (
        <div className="bg-gray-50 p-3 border-t border-gray-200">
          <div className="flex justify-between items-center mb-2">
            <div className="text-xs font-semibold text-gray-700">Interventions</div>
            <button onClick={() => setShowCreate(s => !s)}
                    className="text-xs px-2 py-0.5 bg-emerald-600 text-white rounded font-medium inline-flex items-center gap-1">
              <Plus className="w-3 h-3" /> Add
            </button>
          </div>

          {showCreate && (
            <CreateInterventionForm
              scenarioId={scenario.id}
              onCreated={() => { setShowCreate(false); fetchDetail(); onChanged(); }}
              onCancel={() => setShowCreate(false)}
            />
          )}

          {loading ? (
            <div className="text-xs text-gray-500">Loading…</div>
          ) : interventions.length === 0 ? (
            <div className="text-xs text-gray-500 italic">No interventions yet</div>
          ) : (
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-gray-600">
                  <th className="py-1">Type</th>
                  <th className="py-1">Name</th>
                  <th className="py-1 text-right">Reduction (tCO2e)</th>
                  <th className="py-1 text-right">Cost</th>
                  <th className="py-1 text-right">Payback (yr)</th>
                  <th className="py-1 text-right">Window</th>
                  <th />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {interventions.map(i => (
                  <tr key={i.id}>
                    <td className="py-1">
                      <span className="px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded text-[10px]">
                        {i.intervention_type}
                      </span>
                    </td>
                    <td className="py-1">{i.name}</td>
                    <td className="py-1 text-right font-mono">{Number(i.reduction_potential || 0).toFixed(0)}</td>
                    <td className="py-1 text-right font-mono">{i.cost ? Number(i.cost).toFixed(0) : '—'}</td>
                    <td className="py-1 text-right font-mono">{i.payback_years || '—'}</td>
                    <td className="py-1 text-right text-gray-500">
                      {i.start_year || '?'} – {i.end_year || '?'}
                    </td>
                    <td className="py-1 text-right">
                      <button onClick={() => deleteIntervention(i.id)}
                              className="text-red-500 hover:text-red-700">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

function CreateScenarioForm({ siteId, onCreated, onCancel }) {
  const { showError, showSuccess } = useAlert();
  const cy = new Date().getFullYear();
  const [form, setForm] = useState({
    name: '', description: '',
    baseline_year: cy - 1, target_year: 2030,
    baseline_emissions: '', currency: 'INR',
  });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.name) { showError('Name required'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/mrv/scenarios`, {
        site_id: siteId || null,
        name: form.name,
        description: form.description || null,
        baseline_year: form.baseline_year ? parseInt(form.baseline_year) : null,
        target_year: form.target_year ? parseInt(form.target_year) : null,
        baseline_emissions: form.baseline_emissions ? parseFloat(form.baseline_emissions) : null,
        currency: form.currency,
      });
      showSuccess('Scenario created');
      onCreated();
    } catch (e) {
      showError(e.response?.data?.detail || 'Create failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mb-4 p-4 border border-emerald-200 bg-emerald-50 rounded-md">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
        <input placeholder="Scenario name" value={form.name}
               onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
               className="px-2 py-1 text-sm border rounded sm:col-span-2" />
        <input type="number" placeholder="Baseline year" value={form.baseline_year}
               onChange={e => setForm(f => ({ ...f, baseline_year: e.target.value }))}
               className="px-2 py-1 text-sm border rounded" />
        <input type="number" placeholder="Target year" value={form.target_year}
               onChange={e => setForm(f => ({ ...f, target_year: e.target.value }))}
               className="px-2 py-1 text-sm border rounded" />
        <input placeholder="Description" value={form.description}
               onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
               className="px-2 py-1 text-sm border rounded sm:col-span-2" />
        <input type="number" step="any" placeholder="Baseline emissions (tCO2e)"
               value={form.baseline_emissions}
               onChange={e => setForm(f => ({ ...f, baseline_emissions: e.target.value }))}
               className="px-2 py-1 text-sm border rounded" />
        <input placeholder="Currency" value={form.currency}
               onChange={e => setForm(f => ({ ...f, currency: e.target.value }))}
               className="px-2 py-1 text-sm border rounded" />
      </div>
      <div className="mt-2 flex gap-2 justify-end">
        <button onClick={onCancel} className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
        <button onClick={submit} disabled={saving}
                className="px-3 py-1 bg-emerald-600 text-white rounded text-sm font-medium disabled:bg-gray-300">
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}

function CreateInterventionForm({ scenarioId, onCreated, onCancel }) {
  const { showError, showSuccess } = useAlert();
  const cy = new Date().getFullYear();
  const [form, setForm] = useState({
    intervention_type: 'renewable_energy', name: '', description: '',
    reduction_potential: '', cost: '', payback_years: '',
    start_year: cy + 1, end_year: cy + 5,
  });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.name) { showError('Name required'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/mrv/scenarios/${scenarioId}/interventions`, {
        intervention_type: form.intervention_type,
        name: form.name,
        description: form.description || null,
        reduction_potential: form.reduction_potential ? parseFloat(form.reduction_potential) : null,
        cost: form.cost ? parseFloat(form.cost) : null,
        payback_years: form.payback_years ? parseFloat(form.payback_years) : null,
        start_year: form.start_year ? parseInt(form.start_year) : null,
        end_year: form.end_year ? parseInt(form.end_year) : null,
      });
      showSuccess('Intervention added');
      onCreated();
    } catch (e) {
      showError(e.response?.data?.detail || 'Create failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mb-2 p-2 bg-white border border-gray-200 rounded">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
        <select value={form.intervention_type}
                onChange={e => setForm(f => ({ ...f, intervention_type: e.target.value }))}
                className="px-1.5 py-1 text-xs border rounded">
          {INTERVENTION_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <input placeholder="Name" value={form.name}
               onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
               className="px-1.5 py-1 text-xs border rounded" />
        <input type="number" step="any" placeholder="Reduction (tCO2e)"
               value={form.reduction_potential}
               onChange={e => setForm(f => ({ ...f, reduction_potential: e.target.value }))}
               className="px-1.5 py-1 text-xs border rounded" />
        <input type="number" step="any" placeholder="Cost"
               value={form.cost}
               onChange={e => setForm(f => ({ ...f, cost: e.target.value }))}
               className="px-1.5 py-1 text-xs border rounded" />
        <input type="number" step="any" placeholder="Payback yrs"
               value={form.payback_years}
               onChange={e => setForm(f => ({ ...f, payback_years: e.target.value }))}
               className="px-1.5 py-1 text-xs border rounded" />
        <input type="number" placeholder="Start year"
               value={form.start_year}
               onChange={e => setForm(f => ({ ...f, start_year: e.target.value }))}
               className="px-1.5 py-1 text-xs border rounded" />
        <input type="number" placeholder="End year"
               value={form.end_year}
               onChange={e => setForm(f => ({ ...f, end_year: e.target.value }))}
               className="px-1.5 py-1 text-xs border rounded" />
        <input placeholder="Description" value={form.description}
               onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
               className="px-1.5 py-1 text-xs border rounded" />
      </div>
      <div className="mt-1 flex gap-1 justify-end">
        <button onClick={onCancel} className="px-2 py-0.5 text-xs text-gray-600 hover:text-gray-800">Cancel</button>
        <button onClick={submit} disabled={saving}
                className="px-2 py-0.5 bg-emerald-600 text-white rounded text-xs font-medium disabled:bg-gray-300">
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}
